import { useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Select, Table, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { AlertOctagon, CheckCircle2, Clock, Phone, RotateCcw, Search, ShieldAlert, Smile } from 'lucide-react';
import Layout from '../../Layout';
import {
  COMPLAINT_CATEGORY, SEVERITY, complaintMetaOf, isDoNotCall, taskText, useCallCenter,
  type Callback, type ComplaintCategory, type Severity,
} from '../CustomerDemo/callCenterStore';
import { softphone } from '../CustomerDemo/softphone';
import Softphone from '../CustomerDemo/components/Softphone';
import { AssigneeSelect, TaskBadges, TaskDoneButton } from '../CustomerDemo/components/TaskWidgets';
import { COMPLAINT_STATUS, complaintStats, complaintStatusOf, complaintsOf, type ComplaintStatus } from './cskhStats';
import { CustomerByPhone, Kpi, PageHeader, dueText, fmtTime } from './shared';
import { useCskhDemoAutoload } from './demoData';

type StatusParam = ComplaintStatus | 'active' | 'all';
const STATUS_OPTIONS: { value: StatusParam; label: string }[] = [
  { value: 'active', label: 'Chưa đóng' },
  { value: 'overdue', label: COMPLAINT_STATUS.overdue },
  { value: 'open', label: 'Trong hạn' },
  { value: 'done', label: COMPLAINT_STATUS.done },
  { value: 'all', label: 'Tất cả' },
];
const STATUS_BADGE: Record<ComplaintStatus, string> = {
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  overdue: 'bg-rose-50 text-rose-600 border-rose-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

/** Theo dõi khiếu nại: SLA theo mức độ, chuyển quản lý, đóng kèm mức hài lòng của khách */
export default function CskhComplaintsPage() {
  useCskhDemoAutoload();
  const s = useCallCenter();
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') as StatusParam) || 'active';
  const category = (params.get('category') as ComplaintCategory) || null;
  const severity = (params.get('severity') as Severity) || null;
  const q = params.get('q') || '';
  const go = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };

  const now = Date.now();
  const stats = complaintStats(s, now);
  const list = complaintsOf(s, { status: status === 'all' ? null : status, category, severity, q }, now);
  const hasFilter = !!(category || severity || q || status !== 'active');

  const columns: ColumnsType<Callback> = [
    { title: 'KHÁCH HÀNG', key: 'c', width: 220, render: (_, cb) => <CustomerByPhone phone={cb.phone} /> },
    {
      title: 'NỘI DUNG KHIẾU NẠI', key: 'content',
      render: (_, cb) => (
        <div className="min-w-[240px] max-w-[440px]">
          <div className="flex flex-wrap gap-1 mb-1"><TaskBadges cb={cb} /></div>
          <div className="text-[13px] text-gray-700 dark:text-gray-300">{taskText(cb) || '—'}</div>
          <div className="text-[11px] text-gray-400 mt-0.5">Ghi nhận bởi {cb.by} · {fmtTime(cb.createdAt, now)}</div>
          {cb.escalated && <div className="text-[11px] text-purple-600">Chuyển quản lý bởi {cb.escalated.by} · {fmtTime(cb.escalated.at, now)}</div>}
        </div>
      ),
    },
    {
      title: 'TRẠNG THÁI / SLA', key: 'sla', width: 170,
      render: (_, cb) => {
        const st = complaintStatusOf(cb, now);
        return (
          <div className="space-y-1">
            <span className={`text-[11px] px-1.5 py-0.5 rounded border ${STATUS_BADGE[st]}`}>{COMPLAINT_STATUS[st]}</span>
            <div className="text-xs text-gray-500">Hạn {dayjs(cb.dueAt).format('HH:mm DD/MM')}</div>
            {!cb.doneAt && <div className={`text-[11px] ${st === 'overdue' ? 'text-rose-600 font-semibold' : 'text-gray-400'}`}>{dueText(cb.dueAt, now)}</div>}
          </div>
        );
      },
    },
    { title: 'NGƯỜI XỬ LÝ', key: 'who', width: 170, render: (_, cb) => (cb.doneAt ? <span className="text-xs text-gray-500">{cb.doneBy}</span> : <AssigneeSelect cb={cb} />) },
    {
      title: 'KẾT QUẢ', key: 'result', width: 220,
      render: (_, cb) => cb.doneAt ? (
        <div className="text-xs">
          <div className={cb.satisfied ? 'text-emerald-600 font-medium' : 'text-rose-600 font-medium'}>{cb.satisfied ? 'Khách hài lòng' : 'Khách chưa hài lòng → mở lại'}</div>
          <div className="text-gray-500 line-clamp-2">{cb.result}</div>
          <div className="text-gray-400">{fmtTime(cb.doneAt, now)}</div>
        </div>
      ) : <span className="text-gray-300">—</span>,
    },
    {
      title: '', key: 'act', width: 220, fixed: 'right',
      render: (_, cb) => cb.doneAt ? null : (
        <div className="flex flex-wrap items-center gap-1.5 justify-end">
          <Tooltip title={isDoNotCall(s, cb.phone) ? 'Khách đang chặn gọi' : undefined}>
            <Button size="small" type="primary" icon={<Phone size={12} />} disabled={isDoNotCall(s, cb.phone)} onClick={() => softphone.dial(cb.phone, { callbackId: cb.id })}>Gọi</Button>
          </Tooltip>
          <TaskDoneButton cb={cb} />
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Khiếu nại" desc={`Hạn xử lý theo mức độ: ${(Object.keys(SEVERITY) as Severity[]).map(k => `${SEVERITY[k].label} ${SEVERITY[k].slaHours}h`).join(' · ')}. Quá hạn thì chuyển quản lý.`} />

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <Kpi icon={<AlertOctagon size={18} />} tone="bg-amber-50 text-amber-600" label="Đang mở" value={stats.open}
            hint={`${stats.escalated} đã chuyển quản lý`} onClick={() => go({ status: null })} active={status === 'active'} />
          <Kpi icon={<Clock size={18} />} tone="bg-rose-50 text-rose-600" label="Quá hạn SLA" value={stats.overdue}
            hint="Cần xử lý / chuyển cấp ngay" onClick={() => go({ status: 'overdue' })} active={status === 'overdue'} />
          <Kpi icon={<CheckCircle2 size={18} />} tone="bg-emerald-50 text-emerald-600" label="Đã đóng" value={stats.closed}
            hint={stats.onTimeRate === null ? 'Chưa có' : `${stats.onTimeRate}% đóng đúng hạn`} onClick={() => go({ status: 'done' })} active={status === 'done'} />
          <Kpi icon={<Smile size={18} />} tone="bg-blue-50 text-blue-600" label="Khách hài lòng sau xử lý" value={stats.satisfiedRate === null ? '—' : `${stats.satisfiedRate}%`}
            hint="Trên các khiếu nại đã đóng" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input allowClear className="!w-full sm:!w-64" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm tên, số điện thoại, nội dung"
            value={q} onChange={e => go({ q: e.target.value || null })} />
          <Select className="w-full sm:w-[150px]" value={status} onChange={v => go({ status: v === 'active' ? null : v })} options={STATUS_OPTIONS} />
          <Select allowClear className="w-full sm:w-[190px]" placeholder="Phân loại" value={category || undefined} onChange={v => go({ category: v || null })}
            options={(Object.keys(COMPLAINT_CATEGORY) as ComplaintCategory[]).map(k => ({ value: k, label: COMPLAINT_CATEGORY[k] }))} />
          <Select allowClear className="w-full sm:w-[160px]" placeholder="Mức độ" value={severity || undefined} onChange={v => go({ severity: v || null })}
            suffixIcon={<ShieldAlert size={14} />} options={(Object.keys(SEVERITY) as Severity[]).map(k => ({ value: k, label: SEVERITY[k].label }))} />
          {hasFilter && (
            <button type="button" onClick={() => setParams(new URLSearchParams(), { replace: true })} className="text-xs text-gray-500 hover:text-[#3e91ab] flex items-center gap-1 cursor-pointer">
              <RotateCcw size={12} /> Xoá bộ lọc
            </button>
          )}
        </div>

        <Table columns={columns} dataSource={list} rowKey="id" size="middle" scroll={{ x: 'max-content' }}
          rowClassName={cb => (complaintStatusOf(cb, now) === 'overdue' ? 'bg-rose-50/40 dark:bg-rose-950/10' : complaintMetaOf(cb)?.severity === 'high' && !cb.doneAt ? 'bg-amber-50/40' : '')}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={status === 'active' ? 'Không có khiếu nại đang mở 🎉' : 'Không có khiếu nại nào'} /> }}
          pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: (t, r) => `Hiển thị ${r[0]}–${r[1]} / ${t} khiếu nại` }} />
      </div>
      <Softphone />
    </Layout>
  );
}
